import { AttributeCardValue } from '@/components/Card/AttributeCard';
import { createAddressSchema } from '@/components/Form/AddressForm/-schema/addressSchema';
import { AddressForm } from '@/components/Form/AddressForm/AddressForm';
import { AddressFormContext } from '@/components/Form/AddressForm/AddressFormContext';
import { isSupportedCountry, type SupportedCountry } from '@/constants/country';
import type { AddressValue } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import type { ErrorObject } from '@/utils/error-object';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type Props = Omit<CFormItemProps, 'errors'> & {
  value: AddressValue;
  onChange: (value: AddressValue) => void;
  errorObject?: ErrorObject<AddressValue>;
  inputClassName?: string;
};

/**
 * Card-based Form / AddressItem
 */
export const CFormAddressItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const countryCode = props.value?.countryCode;
  const countryName = countryCode && isSupportedCountry(countryCode) ? tc(`country.${countryCode}`) : countryCode ?? undefined;
  const formattedAddress = [props.value?.streetAddress, props.value?.addressLine2, props.value?.city, props.value?.stateProvinceName, props.value?.postalCode, countryName].filter(e => !!e).join(',');
  const errors = Object.fromEntries(Object.entries(props.errorObject ?? {}).map(([key, value]) => [key, Array.isArray(value) ? value.join(', ') : value]));
  return <CFormItemBase {...props} readComponent={formattedAddress} writeComponent={props.readonly ? <AttributeCardValue>{formattedAddress}</AttributeCardValue> : <AddressFormContext.Provider value={{
    values: {
      countryCode: props.value?.countryCode as SupportedCountry ?? undefined,
      postalCode: props.value?.postalCode ?? undefined,
      stateProvinceCode: props.value?.stateProvinceCode ?? undefined,
      city: props.value?.city ?? undefined,
      streetAddress: props.value?.streetAddress ?? undefined,
      addressLine2: props.value?.addressLine2 ?? undefined
    },
    errors: errors,
    touched: {
      countryCode: true,
      postalCode: true,
      stateProvinceCode: true,
      city: true,
      streetAddress: true,
      addressLine2: true
    },
    onChange: changes => {
      const address = {
        ...props.value,
        ...changes
      };
      props.onChange(address);
    },
    onBlur: _key => {},
    validate: address => {
      const schema = createAddressSchema({
        isCountryRequired: true,
        isPostalCodeRequired: true,
        isStateProvinceRequired: true,
        isCityRequired: true,
        isStreetAddressRequired: true
      });
      const result = schema.safeParse(address);
      return result.success;
    }
  }}>
            <AddressForm isCountryRequired isPostalCodeRequired isStateProvinceRequired isCityRequired isStreetAddressRequired />
          </AddressFormContext.Provider>} />;
});